"use client";

import React from "react";
import { Info, Send, CircleCheck as CheckCircle, CircleAlert as AlertCircle, FileText, Clock, ShieldCheck } from 'lucide-react';
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
const guidelines = [{
  id: "item-1",
  title: "Apa itu Hak Jawab dan Hak Koreksi?",
  content: "Hak Jawab adalah hak seseorang atau sekelompok orang untuk memberikan tanggapan atau sanggahan terhadap pemberitaan berupa fakta yang merugikan nama baiknya. Hak Koreksi adalah hak setiap orang untuk mengoreksi atau membetulkan kekeliruan informasi yang diberitakan oleh pers, baik tentang dirinya maupun tentang orang lain.",
  icon: <FileText className="size-4 text-primary" />
}, {
  id: "item-2",
  title: "Prosedur Pengajuan",
  content: "Pengajuan Hak Jawab atau Hak Koreksi harus dilakukan secara tertulis melalui formulir di halaman ini atau dikirimkan melalui email resmi redaksi. Pemohon wajib menyertakan identitas diri yang jelas, tautan berita yang dipersoalkan, dan bagian mana yang dianggap tidak akurat beserta fakta yang sebenarnya.",
  icon: <ShieldCheck className="size-4 text-primary" />
}, {
  id: "item-3",
  title: "Batas Waktu Penanganan",
  content: "Redaksi ABADI.NEWS akan memproses setiap pengajuan Hak Jawab dan Hak Koreksi selambat-lambatnya 2x24 jam setelah laporan diterima dan diverifikasi kelengkapannya. Kami berkomitmen untuk melayani setiap aduan dengan profesional dan berimbang.",
  icon: <Clock className="size-4 text-primary" />
}];
function RightOfReplyForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-8 text-center space-y-4">
        <div className="size-12 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-2">
          <CheckCircle className="size-6 text-green-600 dark:text-green-400" />
        </div>
        <h3 className="text-xl font-semibold font-default">Pengajuan Berhasil Terkirim</h3>
        <p className="text-muted-foreground max-w-sm">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-4">
          Kirim Pengajuan Lain
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="fullName" rules={{
        required: "Nama lengkap wajib diisi",
        minLength: {
          value: 3,
          message: "Minimal 3 karakter"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Nama Lengkap / Instansi</FormLabel>
              <FormControl>
                <Input placeholder="Masukkan nama lengkap atau nama instansi" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email wajib diisi",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Format email tidak valid"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Alamat Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="email@contoh.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="phone" rules={{
      required: "Nomor telepon wajib diisi",
      pattern: {
        value: /^[0-9+\-\s()]*$/,
        message: "Format nomor telepon tidak valid"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Nomor Telepon / WhatsApp</FormLabel>
            <FormControl>
              <Input type="tel" placeholder="081234567890" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="articleUrl" rules={{
      required: "Tautan berita wajib diisi",
      pattern: {
        value: /^(http|https):\/\/[^ "]+$/,
        message: "Masukkan URL yang valid (dimulai dengan http:// atau https://)"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Tautan Berita (URL)</FormLabel>
            <FormControl>
              <Input type="url" placeholder="https://abadi.news/..." {...field} />
            </FormControl>
            <FormDescription>
              Salin dan tempel tautan artikel yang ingin Anda tanggapi atau koreksi.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Isi hak jawab/koreksi wajib diisi",
      minLength: {
        value: 20,
        message: "Penjelasan terlalu singkat, minimal 20 karakter"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Isi Hak Jawab / Koreksi</FormLabel>
            <FormControl>
              <Textarea placeholder="Jelaskan bagian mana yang tidak akurat dan sampaikan fakta yang sebenarnya..." className="min-h-[150px] resize-y" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertTitle>Gagal Mengirim</AlertTitle>
          <AlertDescription>{submitError}</AlertDescription>
        </Alert>}

      <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto">
        {isSubmitting ? <>
            <span className="animate-spin mr-2 size-4 border-2 border-current border-t-transparent rounded-lg" />
            Mengirim...
          </> : <>
            <Send className="mr-2 size-4" />
            Kirim Pengajuan
          </>}
      </Button>
    </Form>;
}
export default function RightOfReply() {
  return <section id="right-of-reply" className="py-16 md:py-24 bg-background text-foreground">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        
        <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default tracking-tight mb-4">
            Hak Jawab & Hak Koreksi
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Kami berkomitmen pada jurnalisme yang akurat dan berimbang. Sesuai dengan Undang-Undang Pers dan Kode Etik Jurnalistik, kami menyediakan ruang bagi Anda untuk memberikan tanggapan atau koreksi atas pemberitaan kami.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          
          {}
          <div className="lg:col-span-5 space-y-6">
            <Card className="border-border/50 shadow-sm">
              <CardHeader>
                <CardTitle className="text-xl flex items-center gap-2">
                  <Info className="size-5 text-primary" />
                  Panduan Pengajuan
                </CardTitle>
                <CardDescription>
                  Pelajari prosedur dan ketentuan pengajuan Hak Jawab dan Hak Koreksi di ABADI.NEWS.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full" defaultValue="item-1">
                  {guidelines.map((item, i) => <AccordionItem key={item.id} value={item.id} data-index={i}>
                      <AccordionTrigger className="text-left font-semibold hover:text-primary transition-colors">
                        <span className="flex items-center gap-3">
                          {item.icon}
                          {item.title}
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground leading-relaxed pt-2 pb-4">
                        {item.content}
                      </AccordionContent>
                    </AccordionItem>)}
                </Accordion>
              </CardContent>
            </Card>

            <Alert className="bg-muted/50 border-border/50">
              <AlertCircle className="size-4 text-primary" />
              <AlertTitle className="font-semibold text-foreground">Catatan Penting</AlertTitle>
              <AlertDescription className="text-muted-foreground mt-2">
                Pengajuan yang mengandung unsur SARA, ujaran kebencian, atau tidak berkaitan langsung dengan substansi berita yang dipersoalkan tidak akan kami proses.
              </AlertDescription>
            </Alert>
          </div>

          {}
          <div className="lg:col-span-7">
            <Card className="border-border/50 shadow-md">
              <CardHeader className="pb-4">
                <CardTitle className="text-2xl">Formulir Pengajuan</CardTitle>
                <CardDescription>
                  Silakan lengkapi formulir di bawah ini dengan data yang valid. Tim redaksi kami akan segera menindaklanjuti laporan Anda.
                </CardDescription>
              </CardHeader>
              <Separator className="mb-6" />
              <CardContent>
                <FormProvider formId="right-of-reply-form" sectionName="right-of-reply" defaultValues={{
                fullName: "",
                email: "",
                phone: "",
                articleUrl: "",
                message: ""
              }} submitText="Kirim Pengajuan" successMessage="Terima kasih. Pengajuan Hak Jawab/Koreksi Anda telah kami terima dan akan segera diproses oleh tim redaksi kami.">
                  <RightOfReplyForm />
                </FormProvider>
              </CardContent>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}